/**
 * Word List-Structure Operations
 *
 * Applies structured list changes (level / list membership / restart / detach)
 * to a paragraph via the Office.js List API. These changes are recorded as Track
 * Changes (verified empirically against real Word) and never alter paragraph text.
 *
 * The paragraph primitives here were validated on a throwaway dev bench, then
 * re-parameterized from the current selection to a paragraph resolved by its
 * backend paragraphId.
 */

import type {
  EditOperation,
  EditApplicationResult,
  ListChange,
} from "../../../interfaces/autoRedlineTypes";
import { createLogger } from "../../../utils/logger";
import type { UnifiedIndexTracker } from "./wordIndexAdjustmentTracker";
import { resolveNumberFormat } from "./numberFormatMapping";
import type { ResolvedLevelFormat } from "./numberFormatMapping";
import { resolveParagraphByIndex, resolveParagraphsByIndex } from "./listParagraphResolution";

const log = createLogger("WordListStructureOperations");

/** Outcome of a single list-structure change. */
export interface ListChangeResult {
  success: boolean;
  /** Present when the change was a no-op (e.g. already at the target level). */
  skipped?: boolean;
  error?: string;
}

/**
 * Apply a list-structure change to an already-live paragraph.
 *
 * `anchorParagraph` is the pre-resolved `continueList` anchor. Callers that can
 * resolve it alongside `paragraph` MUST pass it: resolving it here costs a
 * second `body.paragraphs` load, which invalidates `paragraph`. `indexTracker`
 * only backs the fallback resolution for callers that cannot (the INSERT path,
 * whose target is a freshly inserted paragraph rather than an index).
 */
export const applyListChange = async (
  paragraph: Word.Paragraph,
  listChange: ListChange,
  context: Word.RequestContext,
  indexTracker?: UnifiedIndexTracker,
  anchorParagraph?: Word.Paragraph
): Promise<ListChangeResult> => {
  switch (listChange.action) {
    case "setLevel":
      return applySetLevel(paragraph, listChange.level ?? 0, context);
    case "newList":
      return applyNewList(paragraph, listChange, context);
    case "continueList":
      return applyContinueList(paragraph, listChange, context, indexTracker, anchorParagraph);
    case "detach":
      return applyDetach(paragraph, context);
    default:
      return { success: false, error: `Unknown listChange action: ${String(listChange.action)}` };
  }
};

/**
 * Orchestrator for the EDIT path: resolve the target paragraph by paragraphId,
 * apply the change, and map to an EditApplicationResult.
 */
export const applyListChangeEdit = async (
  edit: EditOperation,
  listChange: ListChange,
  context: Word.RequestContext,
  startTime: number,
  paragraphId?: number,
  indexTracker?: UnifiedIndexTracker
): Promise<EditApplicationResult> => {
  if (paragraphId === undefined) {
    return fail(edit.editId, "list-structure edit requires paragraphId", startTime);
  }

  // Target and anchor come out of ONE collection load — see
  // `resolveParagraphsByIndex` for why a second load breaks the first proxy.
  const anchorId = listChange.action === "continueList" ? listChange.anchorParagraphId : undefined;
  const [target, anchor] = await resolveParagraphsByIndex(
    context,
    anchorId === undefined ? [paragraphId] : [paragraphId, anchorId],
    indexTracker
  );

  if (target.outOfBounds || !target.paragraph) {
    return fail(edit.editId, `paragraph ${paragraphId} out of bounds`, startTime);
  }
  if (anchorId !== undefined && (anchor.outOfBounds || !anchor.paragraph)) {
    return fail(edit.editId, `continueList: anchor ${anchorId} out of bounds`, startTime);
  }

  const result = await applyListChange(
    target.paragraph,
    listChange,
    context,
    indexTracker,
    anchor?.paragraph ?? undefined
  );

  if (!result.success) {
    return fail(edit.editId, result.error ?? "list-structure change failed", startTime);
  }
  log.info(`List-structure change applied for ${edit.editId}`, {
    action: listChange.action,
    skipped: result.skipped ?? false,
  });

  return { success: true, editId: edit.editId, duration: Date.now() - startTime };
};

/** Set the paragraph's own list level (must already be a list item). */
const applySetLevel = async (
  paragraph: Word.Paragraph,
  level: number,
  context: Word.RequestContext
): Promise<ListChangeResult> => {
  paragraph.load("isListItem");

  const item = paragraph.listItemOrNullObject;

  item.load("isNullObject, level");
  await context.sync();

  if (!paragraph.isListItem || item.isNullObject) {
    return { success: false, error: "setLevel: target paragraph is not a list item" };
  }

  const next = clampLevel(level);

  if (item.level === next) return { success: true, skipped: true };
  item.level = next;
  await context.sync();

  return { success: true };
};

/**
 * Start a fresh list on the paragraph (forks/restarts; tracked via startNewList).
 *
 * `startNewList()` returns a list with NO level definitions, so EVERY level we
 * might land on — or that a follow-up `continueList` might attach a sibling to —
 * has to be defined before the markers render. The scheme comes from
 * `listChange.numberFormat` (the source list's own definition, lifted from
 * `numbering.xml` by the backend), so the fork inherits its parent's markers
 * rather than always coming out decimal. Without a scheme we seed plain decimal.
 */
const applyNewList = async (
  paragraph: Word.Paragraph,
  listChange: ListChange,
  context: Word.RequestContext
): Promise<ListChangeResult> => {
  const level = clampLevel(listChange.level ?? 0);

  paragraph.load("isListItem");
  await context.sync();

  if (paragraph.isListItem) {
    paragraph.detachFromList();
    await context.sync();
  }

  const list = paragraph.startNewList();

  applyLevelFormats(list, resolveNumberFormat(listChange.numberFormat, level));
  await context.sync();

  if (level > 0) {
    const item = paragraph.listItemOrNullObject;

    item.load("isNullObject");
    await context.sync();

    if (!item.isNullObject) {
      item.level = level;
      await context.sync();
    }
  }

  return { success: true };
};

/**
 * Define each resolved level's marker on a list.
 *
 * Queued onto the caller's batch — the caller owns the `context.sync()`. Note
 * these are FORMAT mutations, which Word applies silently rather than as
 * accept/reject-able revisions; the tracked part of a fork is the list
 * membership change, not the markers.
 */
const applyLevelFormats = (list: Word.List, levels: ResolvedLevelFormat[]): void => {
  for (const entry of levels) {
    if (entry.kind === "bullet") {
      list.setLevelBullet(entry.level, Word.ListBullet[entry.bullet]);
    } else {
      list.setLevelNumbering(entry.level, Word.ListNumbering[entry.numbering], entry.formatString);
    }
  }
};

/**
 * Join the paragraph to the anchor paragraph's list at `level`.
 *
 * Every property both paragraphs need is loaded in ONE sync. The previous shape
 * — resolve anchor (collection load + sync), read its list (sync), then read the
 * target's own `isListItem` (sync) — left `paragraph` spanning a collection
 * re-load, which is what Word for Mac rejected with "GeneralException at
 * Document._GetObjectByReferenceId".
 */
const applyContinueList = async (
  paragraph: Word.Paragraph,
  listChange: ListChange,
  context: Word.RequestContext,
  indexTracker?: UnifiedIndexTracker,
  anchorParagraph?: Word.Paragraph
): Promise<ListChangeResult> => {
  const anchorId = listChange.anchorParagraphId;

  if (anchorId === undefined) {
    return { success: false, error: "continueList: anchorParagraphId is required" };
  }

  // Fallback only for callers that cannot pre-resolve (the INSERT path).
  const anchor =
    anchorParagraph ?? (await resolveParagraphByIndex(context, anchorId, indexTracker)).paragraph;

  if (!anchor) {
    return { success: false, error: `continueList: anchor ${anchorId} out of bounds` };
  }

  const anchorList = anchor.listOrNullObject;
  const anchorItem = anchor.listItemOrNullObject;

  paragraph.load("isListItem");
  anchor.load("isListItem");
  anchorList.load("isNullObject, id");
  anchorItem.load("isNullObject, level");
  await context.sync();

  if (!anchor.isListItem || anchorList.isNullObject) {
    return { success: false, error: `continueList: anchor ${anchorId} is not a list item` };
  }

  const level = clampLevel(listChange.level ?? (anchorItem.isNullObject ? 0 : anchorItem.level));

  // The detach commits on its own sync, so an attach failure leaves the paragraph
  // in no list. We deliberately do NOT compensate — a re-attach is itself a Word
  // call on a context that just threw, so it risks turning one bad state into
  // two. We report what the document actually holds instead.
  let detached = false;

  try {
    if (paragraph.isListItem) {
      paragraph.detachFromList();
      await context.sync();
      detached = true;
    }
    paragraph.attachToList(anchorList.id, level);
    await context.sync();
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);

    log.error("continueList: attach failed", { error: errorMsg, listId: anchorList.id, level });

    const partial = detached
      ? " The paragraph was removed from its original list and is now unnumbered — its numbering needs to be reapplied."
      : "";

    return { success: false, error: `continueList attach failed: ${errorMsg}.${partial}` };
  }

  return { success: true };
};

/** Remove the paragraph from its list. */
const applyDetach = async (
  paragraph: Word.Paragraph,
  context: Word.RequestContext
): Promise<ListChangeResult> => {
  paragraph.load("isListItem");
  await context.sync();

  if (!paragraph.isListItem) return { success: true, skipped: true };
  paragraph.detachFromList();
  await context.sync();

  return { success: true };
};

const clampLevel = (level: number): number => Math.min(8, Math.max(0, level));

const fail = (editId: string, error: string, startTime: number): EditApplicationResult => {
  log.warn(`List-structure edit ${editId} failed: ${error}`);

  return { success: false, editId, error, duration: Date.now() - startTime };
};
